import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, skipToken, useQuery } from "@tanstack/react-query";
import {
  Clock,
  Languages,
  MessageSquareText,
  MessagesSquare,
  Mic,
  RefreshCw,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import {
  Badge,
  Button,
  DefinitionList,
  DetailRow,
  EmptyState,
  MasterDetailShell,
  masterDetailListItemClass,
  PageHeader,
  Pagination,
  pageLayoutClass,
  type BadgeVariant,
  brandFillClass,
} from "@/components/ui";
import { ErrorState, ListResultsCard } from "@/components/common";
import { buildDataTableLabels } from "@/components/table";
import { formatDateTime } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import {
  agentVoiceTranscriptKeys,
  agentVoiceTranscriptService,
  recommendedActivityNamesFromTurns,
  type AgentTranscriptEndedWith,
  type AgentVoiceTranscriptDetail,
  type AgentVoiceTranscriptListItem,
} from "@/services/agentVoiceTranscriptService";

const PAGE_SIZE = 20;

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function endedBadgeVariant(endedWith: AgentTranscriptEndedWith): BadgeVariant {
  if (endedWith === "error") return "error";
  if (endedWith === "time_limit") return "warning";
  return "default";
}

function endedTileClass(endedWith: AgentTranscriptEndedWith): string {
  if (endedWith === "error") {
    return "bg-error-50 text-error-600 ring-error-600/15";
  }
  if (endedWith === "time_limit") {
    return "bg-warning-50 text-warning-600 ring-warning-600/20";
  }
  return "bg-primary-50 text-primary-600 ring-primary-600/15";
}

function MetaChip({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-surface-100 px-2 py-0.5 text-xs font-medium text-surface-600">
      <Icon className="h-3.5 w-3.5 text-surface-400" aria-hidden />
      <span className="tabular-nums">{children}</span>
    </span>
  );
}

function TranscriptListItem({
  item,
  selected,
  onSelect,
}: {
  item: AgentVoiceTranscriptListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("adminAgentTranscripts");
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        masterDetailListItemClass(selected),
        "relative flex-row items-start gap-3 border-b border-surface-100 dark:hover:bg-surface-100/50",
        selected &&
          "before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-primary-500",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
          endedTileClass(item.ended_with),
        )}
        aria-hidden
      >
        <Mic className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center justify-between gap-2">
          <span className="text-xs tabular-nums text-surface-500">
            {formatDateTime(item.created_at)}
          </span>
          <Badge variant={endedBadgeVariant(item.ended_with)} size="sm">
            {t(`endedWith.${item.ended_with}`)}
          </Badge>
        </span>
        <span className="line-clamp-2 text-sm font-medium text-surface-900">
          {item.preview_text ?? t("meta.none")}
        </span>
        <span className="text-xs tabular-nums text-surface-500">
          {formatDuration(item.duration_ms)} · {item.turn_count}{" "}
          {t("meta.turns").toLowerCase()} · {item.language.toUpperCase()}
        </span>
      </span>
    </button>
  );
}

function TranscriptDetail({
  detail,
}: {
  detail: AgentVoiceTranscriptDetail;
}) {
  const { t } = useTranslation("adminAgentTranscripts");
  const recommendedNames = recommendedActivityNamesFromTurns(detail.turns);

  return (
    <div className="flex h-full min-h-0 flex-col animate-fade-in">
      <header className="space-y-4 border-b border-surface-200 px-5 py-4">
        <div className="flex items-start gap-3.5">
          <span
            className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-glow ring-1 ring-inset ring-white/15", brandFillClass("primary"))}
            aria-hidden
          >
            <Mic className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[17px] font-semibold tracking-tight text-surface-900 tabular-nums">
                {formatDateTime(detail.created_at)}
              </h2>
              <Badge variant={endedBadgeVariant(detail.ended_with)} size="sm" dot>
                {t(`endedWith.${detail.ended_with}`)}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <MetaChip icon={Languages}>
                {t("meta.language")}: {detail.language.toUpperCase()}
              </MetaChip>
              <MetaChip icon={Clock}>
                {t("meta.duration")}: {formatDuration(detail.duration_ms)}
              </MetaChip>
              <MetaChip icon={MessagesSquare}>
                {t("meta.turns")}: {detail.turns.filter((turn) => turn.role !== "recommendations").length}
              </MetaChip>
            </div>
          </div>
        </div>
        <DefinitionList>
          <DetailRow
            label={t("meta.activities")}
            valueClassName="mt-1.5 flex flex-wrap gap-1.5"
          >
            {recommendedNames.length === 0 ? (
              <span className="text-[13px] text-surface-500">{t("meta.none")}</span>
            ) : (
              recommendedNames.map((name) => (
                <Badge key={name} variant="outline">
                  {name}
                </Badge>
              ))
            )}
          </DetailRow>
        </DefinitionList>
      </header>
      <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-surface-50/60 px-5 py-5">
        {detail.turns.map((turn, index) => {
          if (turn.role === "recommendations") {
            return (
              <li
                key={`${turn.role}-${index}`}
                className="mx-auto w-full max-w-xl rounded-lg border border-dashed border-accent-300 bg-accent-50/60 px-3.5 py-2.5"
              >
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-accent-800">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  {t("roles.recommendations")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {turn.activity_names.length === 0 ? (
                    <span className="text-sm text-surface-600">
                      {t("meta.none")}
                    </span>
                  ) : (
                    turn.activity_names.map((name) => (
                      <Badge key={`${index}-${name}`} variant="outline">
                        {name}
                      </Badge>
                    ))
                  )}
                </div>
              </li>
            );
          }
          const isGuest = turn.role === "user";
          return (
            <li
              key={`${turn.role}-${index}`}
              className={cn("flex", isGuest ? "justify-start" : "justify-end")}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed shadow-xs",
                  isGuest
                    ? "rounded-tl-sm border border-surface-200 bg-surface-0 text-surface-900"
                    : "rounded-tr-sm bg-primary-fill text-white",
                )}
              >
                <p
                  className={cn(
                    "mb-0.5 text-[11px] font-medium",
                    isGuest ? "text-surface-500" : "text-white",
                  )}
                >
                  {t(`roles.${turn.role}`)}
                </p>
                <p className="whitespace-pre-wrap">{turn.text}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function AdminAgentTranscriptsPage() {
  const { t } = useTranslation("adminAgentTranscripts");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const skip = (page - 1) * PAGE_SIZE;

  const listQuery = useQuery({
    queryKey: agentVoiceTranscriptKeys.list(skip, PAGE_SIZE),
    queryFn: () =>
      agentVoiceTranscriptService.list({ skip, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const detailQuery = useQuery({
    queryKey: selectedId
      ? agentVoiceTranscriptKeys.detail(selectedId)
      : ["admin", "agent-voice-transcripts", "detail", "none"],
    queryFn: selectedId
      ? () => agentVoiceTranscriptService.get(selectedId)
      : skipToken,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={cn(pageLayoutClass, "min-h-0")}>
      <PageHeader
        title={t("title")}
        description={t("description")}
        icon={MessageSquareText}
        actions={
          <Button
            variant="outline"
            isLoading={listQuery.isFetching}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => void listQuery.refetch()}
          >
            {tCommon("refresh")}
          </Button>
        }
      />

      <MasterDetailShell
        list={
          <ListResultsCard
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            hideHeader
            contentClassName="flex min-h-0 flex-1 flex-col p-0"
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            errorState={
              <ErrorState
                className="px-5 py-8"
                title={t("loadError")}
                onRetry={() => void listQuery.refetch()}
              />
            }
            isEmpty={items.length === 0}
            emptyState={
              <EmptyState
                className="px-5 py-8"
                icon={MessageSquareText}
                title={t("empty")}
              />
            }
            pagination={
              totalPages > 1 ? (
                <Pagination
                  labels={tableLabels}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  showPageNumbers
                  isLoading={listQuery.isFetching}
                  total={total}
                  placement="standalone"
                  className="shrink-0 border-t border-surface-100 p-3"
                />
              ) : undefined
            }
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              {items.map((item) => (
                <TranscriptListItem
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}
            </div>
          </ListResultsCard>
        }
        detail={
          <ListResultsCard
            className="flex min-h-0 h-full flex-col overflow-hidden"
            hideHeader
            contentClassName="flex min-h-0 flex-1 flex-col p-0"
            isEmpty={!selectedId}
            emptyState={
              <EmptyState
                className="px-6 py-16"
                icon={MessagesSquare}
                title={t("selectPrompt")}
              />
            }
            isLoading={selectedId != null && detailQuery.isLoading}
            isError={
              selectedId != null &&
              (detailQuery.isError || detailQuery.data == null) &&
              !detailQuery.isLoading
            }
            errorState={
              <ErrorState
                className="px-6 py-10"
                title={t("detailLoadError")}
                onRetry={() => void detailQuery.refetch()}
              />
            }
          >
            {detailQuery.data ? (
              <TranscriptDetail detail={detailQuery.data} />
            ) : null}
          </ListResultsCard>
        }
      />
    </div>
  );
}
